import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { authAPI } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { logger } from '@/lib/logger'
import { getApiError } from '@/lib/utils'

interface MfaSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Personal TOTP management: enroll (QR + first code) or disable (code). */
export function MfaSettingsDialog({ open, onOpenChange }: MfaSettingsDialogProps) {
  const user = useAuthStore((s) => s.user)
  const mfaOn = Boolean(user?.mfa_enabled)
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauth_uri: string } | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  const setUserMfa = (enabled: boolean) => {
    const state = useAuthStore.getState()
    if (state.user) {
      useAuthStore.setState({ user: { ...state.user, mfa_enabled: enabled } })
    }
  }

  const close = () => {
    setEnrollment(null)
    setCode('')
    setError(null)
    onOpenChange(false)
  }

  const enrollMutation = useMutation({
    mutationFn: () => authAPI.mfaEnroll(),
    onSuccess: (response) => setEnrollment(response.data),
    onError: (err) => setError(getApiError(err, 'Could not start MFA setup')),
  })

  const confirmMutation = useMutation({
    mutationFn: () => authAPI.mfaEnrollConfirm(code),
    onSuccess: () => {
      logger.success('MfaSettingsDialog', 'MFA enabled')
      setUserMfa(true)
      close()
    },
    onError: (err) => setError(getApiError(err, 'Wrong code. Try again.')),
  })

  const disableMutation = useMutation({
    mutationFn: () => authAPI.mfaDisable(code),
    onSuccess: () => {
      logger.success('MfaSettingsDialog', 'MFA disabled')
      setUserMfa(false)
      close()
    },
    onError: (err) => setError(getApiError(err, 'Wrong code. Try again.')),
  })

  const busy = enrollMutation.isPending || confirmMutation.isPending || disableMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Two-Factor Authentication</DialogTitle>
          <DialogDescription>
            {mfaOn
              ? 'MFA is on. Enter a current code to turn it off.'
              : 'Add a second factor: scan a QR code with an authenticator app.'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        {!mfaOn && !enrollment && (
          <Button onClick={() => enrollMutation.mutate()} disabled={busy}>
            {enrollMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Start setup
          </Button>
        )}

        {!mfaOn && enrollment && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-2">
              <div className="rounded-lg bg-white p-3 border">
                <QRCodeSVG value={enrollment.otpauth_uri} size={168} />
              </div>
              <p className="text-xs text-muted-foreground break-all text-center">
                Manual key: <span className="font-mono">{enrollment.secret}</span>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfa-confirm-code">Code from the app</Label>
              <Input
                id="mfa-confirm-code"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <Button
              className="w-full"
              onClick={() => confirmMutation.mutate()}
              disabled={busy || code.length !== 6}
            >
              {confirmMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm and enable
            </Button>
          </div>
        )}

        {mfaOn && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mfa-disable-code">Current code</Label>
              <Input
                id="mfa-disable-code"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => disableMutation.mutate()}
              disabled={busy || code.length !== 6}
            >
              {disableMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Turn off MFA
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
